import { LinearProgress, Typography, makeStyles } from '@material-ui/core'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import AIIcon from 'resources/AIIcon'
import type { SpecIntelligenceRunStatus } from 'api/graphQL/types/specIntelligence.types'
import React from 'react'

const useStyles = makeStyles(() => ({
    '@keyframes starSpin': {
        '0%': { transform: 'rotate(0deg)' },
        '10%': { transform: 'rotate(113deg)' },
        '20%': { transform: 'rotate(216deg)' },
        '30%': { transform: 'rotate(309deg)' },
        '40%': { transform: 'rotate(395deg)' },
        '50%': { transform: 'rotate(477deg)' },
        '60%': { transform: 'rotate(563deg)' },
        '70%': { transform: 'rotate(656deg)' },
        '80%': { transform: 'rotate(824deg)' },
        '90%': { transform: 'rotate(1088deg)' },
        '100%': { transform: 'rotate(1440deg)' },
    },
    '@keyframes starPulse': {
        '0%': { transform: 'scale(0.9)' },
        '50%': { transform: 'scale(1.12)' },
        '100%': { transform: 'scale(0.9)' },
    },
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        padding: `${pxToRem(32)} ${pxToRem(24)}`,
        gap: pxToRem(20),
        textAlign: 'center',
    },
    iconCircle: {
        width: pxToRem(56),
        height: pxToRem(56),
        borderRadius: '50%',
        backgroundColor: '#E3F2FD',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#1565C0',
    },
    icon: {
        width: pxToRem(32),
        height: pxToRem(32),
        animation: '$starPulse 2.4s ease-in-out infinite',
        '& path': {
            transformBox: 'fill-box',
            transformOrigin: 'center',
            animation: '$starSpin 6.8s linear infinite',
        },
        '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
            '& path': { animation: 'none' },
        },
    },
    title: {
        fontSize: pxToRem(15),
        fontWeight: 600,
        color: '#04325B',
    },
    progressBar: {
        width: '100%',
        height: pxToRem(6),
        borderRadius: pxToRem(3),
    },
    progressLabel: {
        fontSize: pxToRem(13),
        color: '#757575',
    },
    errorLabel: {
        fontSize: pxToRem(13),
        color: '#C62828',
    },
}))

/** Props for {@link SpecIntelligenceProgressView}. */
export interface SpecIntelligenceProgressViewProps {
    /** Current run status from the polling hook; null while waiting for first response. */
    status: SpecIntelligenceRunStatus | null
}

/**
 * Displays a progress bar and status message while a SpecIntelligence run is building.
 * Polling is managed externally by {@link useSpecIntelligencePolling}.
 */
const SpecIntelligenceProgressView = ({ status }: SpecIntelligenceProgressViewProps) => {
    const classes = useStyles()
    const progress = status?.progressPercentage ?? 0

    return (
        <div className={classes.root}>
            <div className={classes.iconCircle} aria-hidden="true">
                <AIIcon className={classes.icon} />
            </div>
            <Typography component="h3" className={classes.title}>Analyzing your project knowledge base...</Typography>
            <LinearProgress
                variant="determinate"
                value={progress}
                className={classes.progressBar}
                aria-label="Analysis progress"
            />
            {status?.state === 'Error' ? (
                <Typography className={classes.errorLabel} role="alert">
                    {status.errorMessage ?? 'An error occurred during analysis.'}
                </Typography>
            ) : (
                <Typography className={classes.progressLabel}>
                    {status?.statusMessage ?? `${progress}% complete`}
                </Typography>
            )}
        </div>
    )
}

export default SpecIntelligenceProgressView
